import { $windowUtils } from "./Window.js";
import { $async } from "./Async.js";

/**
 * Function to handle animated routes.
 * @returns {Object} An object with an `animate` method.
 * @throws {Error} If any error occurs during animation.
 */
export function AnimatedRoutes() {
  return {
    animate: runAnimation,
  };

  /**
   * Asynchronously runs an animation with the provided transition options.
   *
   * @param {Object} transition - The transition options for the animation.
   * @throws {Error} Throws an error if any of the required functions (captureSizeAsync, createScreen, setTimeout, scrollTop) are not available.
   */
  async function runAnimation(transition) {
    const contentSize = await captureSizeAsync();
    const screenElement = createScreen();
    const options = { ...contentSize, transition, screenElement };

    if (options.transition.firstRender) {
      return;
    }

    options.element.style.display = "none";
    options.screenElement.style.opacity = 1;
    options.screenElement.style.pointerEvents = "auto";

    $async.setTimeout(() => {
      options.screenElement.style.opacity = 0;
      options.screenElement.style.pointerEvents = "none";
      options.element.style.display = "block";
      $windowUtils.scrollTop();
      setTimeout(async function () {
        await $windowUtils.packageLoader.tailwindIntersect();
      }, 1);
    }, options.transition.transitionDurationMilliseconds);
  }

  /**
   * Asynchronously captures the size using a Promise.
   *
   * @returns {Promise} A Promise that resolves with the captured content size.
   * @throws {Error} If there is an error capturing the content size.
   */
  function captureSizeAsync() {
    return new Promise((resolver) => {
      $async.setTimeout(() => {
        const contentSize = $windowUtils.captureContentSize();
        resolver(contentSize);
      });
    });
  }

  /**
   * Creates a screen element and appends it to the document body if it does not already exist.
   * @returns {HTMLElement} The screen element.
   * @throws {Error} If there is an issue creating or appending the screen element.
   */
  function createScreen() {
    const existingElement = document.getElementById("screenElement");
    if (existingElement) {
      return existingElement;
    }
    const screenElement = document.createElement("div");
    screenElement.id = "screenElement";
    screenElement.style.height = "100lvh";
    screenElement.style.width = "100lvw";
    screenElement.style.position = "absolute";
    screenElement.style.background = "#fff";
    screenElement.style.top = 0;
    screenElement.style.left = 0;
    screenElement.style.zIndex = 11;
    screenElement.style.opacity = 0;
    screenElement.style.pointerEvents = "none";
    screenElement.style.transitionProperty = "opacity";
    screenElement.style.transitionDuration = "250ms";
    screenElement.style.transitionTimingFunction = "linear";
    screenElement.style.transitionDelay = "0s";
    screenElement.style.cursor = "wait";
    document.body.appendChild(screenElement);
    return screenElement;
  }
}
